'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

const STORAGE_KEY = 'als_pv_sid';
const ENDPOINT = '/api/analytics/pageview';

function getOrCreateSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const existing = window.sessionStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const fresh =
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
    window.sessionStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage may throw in privacy mode — beacon becomes a no-op.
    return null;
  }
}

function sendBeacon(path: string, sessionId: string): void {
  const payload = JSON.stringify({ path, sessionId });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
  } catch {
    // fall through
  }
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // swallow
  });
}

export function PageViewBeacon() {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) return;
    // Admin routes are tracked separately and shouldn't pollute public analytics.
    // The beacon endpoint also drops these server-side, but skipping client-side saves the round-trip.
    if (pathname.startsWith('/admin')) return;
    const sid = getOrCreateSessionId();
    if (!sid) return;
    sendBeacon(pathname, sid);
  }, [pathname]);

  return null;
}
